"use client";

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Loader2,
  Upload,
  X,
  FileText,
  Image,
  Plus,
  CheckCircle,
  Search,
  Check,
  Camera,
} from "lucide-react";
import moment from "moment";
import { parseAppDateTime } from "../utils/timezone";
import pettyCashService from "../apis/pettycash";
import { CameraCapture } from "./CameraCapture";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PETTY_CASH_DEPARTMENTS } from "../constants/pettyCashDepartments";

const isHttpUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value.trim());

// DB may store receipts as JSON, double-encoded JSON, a plain URL, or mixed shapes.
const deepParseJson = (value) => {
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    if (typeof current !== "string") break;
    const trimmed = current.trim();
    if (!trimmed) return null;
    if (!(trimmed.startsWith("[") || trimmed.startsWith("{") || trimmed.startsWith('"'))) {
      return trimmed;
    }
    try {
      current = JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }
  return current;
};

const normalizeReceipts = (rawReceipts) => {
  if (!rawReceipts) return [];

  let parsed = deepParseJson(rawReceipts);
  if (parsed == null || parsed === "") return [];

  if (!Array.isArray(parsed)) {
    if (parsed && typeof parsed === "object") {
      parsed = [parsed];
    } else if (typeof parsed === "string") {
      parsed = [parsed];
    } else {
      return [];
    }
  }

  return parsed
    .map((item, index) => {
      if (!item) return null;

      // Nested stringified receipt object/array
      let receipt = deepParseJson(item);
      if (Array.isArray(receipt)) {
        receipt = receipt[0];
      }
      if (!receipt) return null;

      if (typeof receipt === "string") {
        const url = isHttpUrl(receipt) ? receipt.trim() : "";
        if (!url) return null;
        return {
          url,
          originalName: url.split("/").pop() || `Receipt ${index + 1}`,
          uploaded: true,
        };
      }

      if (typeof receipt !== "object") return null;

      const urlCandidate = receipt.url || receipt.secure_url || "";
      // Guard against cases where url itself is still a JSON blob / relative path
      const url = isHttpUrl(urlCandidate)
        ? urlCandidate.trim()
        : isHttpUrl(deepParseJson(urlCandidate)?.url)
          ? deepParseJson(urlCandidate).url.trim()
          : "";

      if (!url) return null;

      return {
        ...receipt,
        url,
        originalName:
          receipt.originalName ||
          receipt.filename ||
          receipt.name ||
          url.split("/").pop() ||
          `Receipt ${index + 1}`,
        uploaded: receipt.uploaded !== false,
      };
    })
    .filter(Boolean);
};

export const PettyCashEditModal = ({
  isOpen,
  onClose,
  transaction,
  onUpdateTransaction,
  currentUser,
}) => {
  const [formData, setFormData] = useState({
    date: "",
    name: "",
    department: "",
    notes: "",
    receipts: [],
    existingReceipts: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showNewUpload, setShowNewUpload] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await pettyCashService.getEmployees();
        const employeeList = Array.isArray(response?.data) ? response.data : [];
        // Backend already puts logged-in user's full profile name first
        if (employeeList[0]) {
          localStorage.setItem("username", employeeList[0]);
          try {
            const profileRaw = localStorage.getItem("profile");
            const profile = profileRaw ? JSON.parse(profileRaw) : {};
            localStorage.setItem(
              "profile",
              JSON.stringify({ ...profile, name: employeeList[0] })
            );
          } catch {
            // ignore
          }
        }
        setEmployees(employeeList);
        setFilteredEmployees(employeeList);
      } catch (error) {
        console.error("Failed to fetch employees", error);
      }
    };
    fetchEmployees();
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = employees.filter((emp) =>
      emp.toLowerCase().includes(value)
    );
    setFilteredEmployees(filtered);
  };
  useEffect(() => {
    if (transaction) {
      const existingReceipts = normalizeReceipts(transaction.receipts);
      const parsedDate = parseAppDateTime(transaction.date);
      const dateValue = parsedDate?.isValid?.()
        ? parsedDate.format("YYYY-MM-DDTHH:mm")
        : moment(transaction.date).isValid()
          ? moment(transaction.date).format("YYYY-MM-DDTHH:mm")
          : "";

      setFormData({
        date: dateValue,
        name: transaction.name || "",
        department: transaction.department || "",
        notes: transaction.notes || "",
        receipts: [],
        existingReceipts,
      });
      // Reset the show new upload state
      setShowNewUpload(false);
    }
  }, [transaction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Improved Cloudinary upload function
  const uploadToCloudinary = async (file) => {
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("upload_preset", "Tumblr"); // Replace with your upload preset
      data.append("cloud_name", "dgqt5ockx"); // Replace with your cloud name

      const response = await axios.post(
        "https://api.cloudinary.com/v1_1/dgqt5ockx/image/upload", // Replace with your cloud name
        data
      );

      return {
        url: response.data.secure_url, // Use secure_url for HTTPS
        publicId: response.data.public_id,
        originalName: file.name,
        size: file.size,
        type: file.type,
        uploaded: true,
      };
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      throw new Error(
        `Failed to upload ${file.name}: ${
          error.response?.data?.error?.message || error.message
        }`
      );
    }
  };
  const handleCameraCapture = async (capturedFile) => {
    try {
      setIsUploading(true);
      const uploadResult = await uploadToCloudinary(capturedFile);

      setFormData(prev => ({
        ...prev,
        receipts: [...prev.receipts, uploadResult]
      }));

      toast.success("Image captured and uploaded successfully!");
    } catch (error) {
      console.error("Error processing captured image:", error);
      toast.error("Failed to process captured image");
    } finally {
      setIsUploading(false);
      setShowCamera(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB per file
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];

    // Validate files
    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 5MB.`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          `File ${file.name} is not a supported format. Please upload images or PDF files.`
        );
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadResults = [];

      // Upload files one by one to better handle errors
      for (const file of validFiles) {
        try {
          if (file.type.startsWith("image/")) {
            // Upload images to Cloudinary
            const uploadResult = await uploadToCloudinary(file);
            uploadResults.push(uploadResult);
            toast.success(`${file.name} uploaded successfully!`);
          } else {
            // For PDFs, keep as local file (you might want to upload these to a different service)
            uploadResults.push({
              file: file,
              originalName: file.name,
              size: file.size,
              type: file.type,
              isLocal: true,
              uploaded: false,
            });
            toast.success(`${file.name} added successfully!`);
          }
        } catch (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
          toast.error(`Failed to upload ${file.name}: ${uploadError.message}`);
          // Continue with other files even if one fails
        }
      }

      if (uploadResults.length > 0) {
        // Add successfully processed files to state
        setFormData((prev) => ({
          ...prev,
          receipts: [...prev.receipts, ...uploadResults],
        }));

        if (uploadResults.length === validFiles.length) {
          toast.success(
            `All ${uploadResults.length} file(s) processed successfully!`
          );
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      setError(error.message || "Failed to upload files");
      toast.error(error.message || "Failed to upload files");
    } finally {
      setIsUploading(false);
      // Reset file input
      e.target.value = "";
    }
  };

  const removeNewFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      receipts: prev.receipts.filter((_, i) => i !== index),
    }));
    toast.success("File removed successfully");
  };

  const removeExistingFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      existingReceipts: prev.existingReceipts.filter((_, i) => i !== index),
    }));
    toast.success("File removed successfully");
  };

  const getFileIcon = (receipt) => {
    // Handle both old format (just filename) and new format (with type)
    let filename = "";
    if (typeof receipt === "string") {
      filename = receipt;
    } else if (receipt.filename) {
      filename = receipt.filename;
    } else if (receipt.originalName) {
      filename = receipt.originalName;
    } else if (receipt.name) {
      filename = receipt.name;
    }

    // Check type first, then fallback to filename extension
    if (receipt.type) {
      if (receipt.type === "application/pdf" || receipt.type.includes("pdf")) {
        return <FileText className="h-4 w-4 text-red-500" />;
      }
      if (receipt.type.startsWith("image/")) {
        return <Image className="h-4 w-4 text-blue-500" />;
      }
    }

    // Fallback to filename extension
    if (filename) {
      const extension = filename.split(".").pop().toLowerCase();
      if (extension === "pdf") {
        return <FileText className="h-4 w-4 text-red-500" />;
      }
    }
    return <Image className="h-4 w-4 text-blue-500" />;
  };

  const handleShowUploadOption = () => {
    setShowNewUpload(true);
  };

  const handleSubmit = async () => {
    if (!transaction) return;

    // Trimmed values
    const name = formData.name.trim();
    const notes = formData.notes.trim();
    const date = formData.date;
    const department = formData.department;

    // Validation
    if (!date) {
      setError("Please select a valid date and time.");
      return;
    }

    if (!name) {
      setError("Please enter the name of the person.");
      return;
    }

    if (!department) {
      setError("Please select a department.");
      return;
    }

    if (!notes) {
      setError("Please enter transaction details.");
      return;
    }

    // Proceed
    setSubmitting(true);

    try {
      const success = await onUpdateTransaction(transaction.id, {
        date: moment(date).toISOString(),
        name,
        department,
        notes,
        existingReceipts: formData.existingReceipts,
        newReceipts: formData.receipts,
        receipts: [...formData.existingReceipts, ...formData.receipts],
      });

      if (success) {
        handleClose();
        toast.success("Transaction updated successfully!");
      }
    } catch (error) {
      console.error("Transaction update error:", error);
      setError("Failed to update transaction. Please try again.");
      toast.error("Failed to update transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      date: "",
      name: "",
      department: "",
      notes: "",
      receipts: [],
      existingReceipts: [],
    });
    setError(null);
    setShowNewUpload(false);
    onClose();
  };

  if (!isOpen || !transaction) return null;

  const departmentOptions =
    formData.department && !PETTY_CASH_DEPARTMENTS.includes(formData.department)
      ? [...PETTY_CASH_DEPARTMENTS, formData.department]
      : PETTY_CASH_DEPARTMENTS;

  // Ensure arrays are always arrays before rendering
  const existingReceipts = Array.isArray(formData.existingReceipts)
    ? formData.existingReceipts
    : [];
  const receipts = Array.isArray(formData.receipts) ? formData.receipts : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {/* Add CameraCapture modal */}
      {showCamera && (
        <CameraCapture
          isOpen={showCamera}
          onClose={() => setShowCamera(false)}
          onCapture={handleCameraCapture}
        />
      )}
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Record</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <Input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full"
            />
          </div>

          {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cash Given To
              </label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full"
              />
            </div> */}
          <div className="mb-4">
            <label className="text-sm font-medium">Cash Given To</label>
            <div className="relative" ref={dropdownRef}>
              <div
                className="border rounded-md p-2 flex justify-between items-center cursor-pointer"
                onClick={() =>
                  setDropdownOpen(
                    dropdownOpen === "employee" ? null : "employee"
                  )
                }
              >
                {formData.name ? (
                  <div className="flex items-center gap-2">
                    <span>{formData.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData((prev) => ({ ...prev, name: "" }));
                      }}
                    >
                      <X size={16} className="text-gray-500" />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400">Select employee</span>
                )}
                <svg
                  className={`w-4 h-4 ml-2 ${
                    dropdownOpen === "employee" ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {dropdownOpen === "employee" && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="py-1">
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp) => (
                        <div
                          key={emp}
                          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, name: emp }));
                            setDropdownOpen(null);
                          }}
                        >
                          <div
                            className={`w-5 h-5 flex items-center justify-center border rounded mr-2 ${
                              formData.name === emp
                                ? "bg-blue-500 text-white"
                                : "border-gray-300"
                              }`}
                          >
                            {formData.name === emp && (
                              <Check className="h-4 w-4" />
                            )}
                          </div>
                          <span>{emp}</span>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-gray-500 text-center">
                        No employees found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <Select
              value={formData.department}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, department: value }))
              }
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {departmentOptions.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Details
            </label>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Receipts
              {isUploading && (
                <span className="text-blue-500 ml-2">(Uploading...)</span>
              )}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Optional here — you can also upload from the Actions column in Transaction History.
            </p>

            {/* Files Already Uploaded Message */}
            {existingReceipts.length > 0 && !showNewUpload && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">
                    Files Already Uploaded ({existingReceipts.length})
                  </span>
                </div>
                <div className="space-y-2 mb-3">
                  {existingReceipts.map((receipt, index) => (
                    <div
                      key={`existing-${index}`}
                      className="flex items-center justify-between bg-white p-2 rounded-md border"
                    >
                      <div className="flex items-center space-x-2">
                        {getFileIcon(receipt)}
                        <span className="text-sm text-gray-700 truncate">
                          {receipt.filename ||
                            receipt.originalName ||
                            receipt.name ||
                            `Receipt ${index + 1}`}
                        </span>
                        {receipt.size && (
                          <span className="text-xs text-gray-500">
                            ({(receipt.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {receipt.url && (
                          <a
                            href={receipt.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700 text-xs underline"
                          >
                            View
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => removeExistingFile(index)}
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                          disabled={isUploading}
                          title="Remove receipt"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-green-200">
                  <button
                    type="button"
                    onClick={handleShowUploadOption}
                    className="flex items-center space-x-2 text-green-700 hover:text-green-800 text-sm font-medium hover:bg-green-100 px-3 py-2 rounded-md transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Upload Additional Files</span>
                  </button>
                </div>
              </div>
            )}

            {/* Show existing receipts in detailed view when uploading new files */}
            {existingReceipts.length > 0 && showNewUpload && (
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-600">
                  Current Receipts ({existingReceipts.length}):
                </span>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {existingReceipts.map((receipt, index) => (
                    <div
                      key={`existing-${index}`}
                      className="flex items-center justify-between bg-blue-50 p-3 rounded-md border"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="flex-shrink-0">
                          {getFileIcon(receipt)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-700 truncate block">
                            {receipt.filename ||
                              receipt.originalName ||
                              receipt.name ||
                              `Receipt ${index + 1}`}
                          </span>
                          {receipt.size && (
                            <span className="text-xs text-gray-500">
                              {(receipt.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex items-center space-x-2">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                            Current
                          </span>
                          {receipt.url && (
                            <a
                              href={receipt.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700 text-xs underline"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExistingFile(index)}
                        className="text-red-500 hover:text-red-700 p-1 ml-2 rounded-full hover:bg-red-50 transition-colors"
                        disabled={isUploading}
                        title="Remove receipt"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload New Receipts - Show when no existing files or when user clicks to upload additional */}
            {(existingReceipts.length === 0 || showNewUpload) && (
              <div className="flex flex-col space-y-4">
                <div className="flex space-x-2">
                  <Button
                    onClick={() => setShowCamera(true)}
                    variant="outline"
                    className="flex items-center space-x-2"
                  >
                    <Camera className="h-4 w-4" />
                    <span>Capture via Camera</span>
                  </Button>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="receipt-upload-edit"
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="receipt-upload-edit"
                      className={`cursor-pointer flex flex-col items-center space-y-2 ${
                        isUploading
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-gray-50"
                        } p-2 rounded-md transition-colors`}
                    >
                      {isUploading ? (
                        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                      ) : (
                        <Upload className="h-8 w-8 text-gray-400" />
                      )}
                      <span className="text-sm text-gray-600 font-medium">
                        {isUploading
                          ? "Uploading receipts..."
                          : existingReceipts.length > 0
                            ? "Click to upload additional receipts"
                            : "Click to upload receipts"}
                      </span>
                      <span className="text-xs text-gray-500">
                        PNG, JPG, GIF, PDF up to 5MB each
                      </span>
                      <span className="text-xs text-green-600 font-medium">
                        Images will be uploaded to cloud storage
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* New Uploaded Files */}
            {receipts.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-600">
                  New Files ({receipts.length}):
                </span>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {receipts.map((receipt, index) => (
                    <div
                      key={`new-${index}`}
                      className="flex items-center justify-between bg-green-50 p-3 rounded-md border"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="flex-shrink-0">
                          {receipt.type?.startsWith("image/") ? (
                            <Image className="h-5 w-5 text-green-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-700 truncate block">
                            {receipt.originalName}
                          </span>
                          <span className="text-xs text-gray-500">
                            {(receipt.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                        <div className="flex-shrink-0">
                          {receipt.uploaded ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                              Uploaded
                            </span>
                          ) : receipt.isLocal ? (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                              Local
                            </span>
                          ) : (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
                              Added
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeNewFile(index)}
                        className="text-red-500 hover:text-red-700 p-1 ml-2 rounded-full hover:bg-red-50 transition-colors"
                        disabled={isUploading}
                        title="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By
            </label>
            <Input
              type="text"
              value={currentUser?.username || "Current User"}
              disabled
              className="w-full bg-gray-100"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting || isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={submitting || isUploading}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
